/*
 * @Author: Auto
 * @Date: 2025-01-XX
 * @Description: 炸金花游戏动画相关代码
 */

import { loadGSAP } from './loadGSAP';
import { playMusic } from './music';

/**
 * 飞筹码动画数据接口
 */
export interface FlyingChip {
    id: number;
    style: Record<string, string>;
}

/**
 * 获取庄家筹码 / 庄家头像的 DOM 区域，用作所有发牌的起点
 */
export const getDealerRect = (): DOMRect | null => {
    if (!import.meta.client) return null;
    const dealerEl = document.querySelector('.dealer-box-img') as HTMLElement;
    if (!dealerEl) return null;
    return dealerEl.getBoundingClientRect();
};

/**
 * 执行一次"从庄家飞到当前玩家"的发牌动画（单张牌）- 优化版本
 * @param cardIndex 牌的索引
 * @param dealerRect 庄家的位置信息
 * @param cardElement 牌元素的引用（可以是 Vue 组件实例，会自动获取 $el）
 */
export const playDealAnimation = (
    cardIndex: number,
    dealerRect: DOMRect,
    cardElement: HTMLElement | any
) => {
    if (!import.meta.client) return;
    const el = (cardElement as any)?.$el ? (cardElement as any).$el : (cardElement as HTMLElement);
    if (!dealerRect || !el) return;

    // 使用 requestAnimationFrame 批量处理，避免强制同步布局
    requestAnimationFrame(() => {
        const cardRect = el.getBoundingClientRect();
        const dealerCenterX = dealerRect.x + 10;
        const dealerCenterY = dealerRect.y + dealerRect.height / 2;
        const cardCenterX = cardRect.left + cardRect.width / 2;
        const cardCenterY = cardRect.top + cardRect.height / 2;

        const dx = dealerCenterX - cardCenterX;
        const dy = dealerCenterY - cardCenterY;

        // 启用 GPU 加速：使用 translate3d 和 will-change 提示浏览器优化
        el.style.willChange = 'transform, opacity';
        el.style.transition = 'none';
        // 使用 translate3d 启用 GPU 硬件加速
        el.style.transform = `translate3d(${dx}px, ${dy}px, 0) scale(0.7)`;
        el.style.opacity = '0';
        
        // 强制重绘，确保初始状态生效
        void el.offsetHeight;
        
        // 使用 requestAnimationFrame 确保初始状态已应用后再开始动画
        requestAnimationFrame(() => {
            el.style.transition = 'transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.35s ease-out';
            el.style.transform = 'translate3d(0, 0, 0) scale(1)';
            el.style.opacity = '1';
            
            // 动画结束后清理 will-change，避免影响后续性能
            setTimeout(() => {
                if (el.style) {
                    el.style.willChange = 'auto';
                }
            }, 350);
        });
    });
};

/**
 * 创建飞筹码动画（支持一次生成多枚筹码）
 * @param fromRect 起始位置
 * @param toRect 目标位置
 * @param delay 延迟时间（毫秒）
 * @param count 筹码数量
 * @param preciseAlignEnd 终点是否精确对齐（用于飞向庄家的筹码）
 * @param preciseAlignStart 起点是否精确对齐（用于从庄家飞出的筹码）
 * @param stageEl 舞台元素引用
 * @param flyingChips 飞筹码数组引用
 * @param flyingChipIdRef 飞筹码ID计数器引用对象（需要包含一个可修改的数字属性）
 */
export const createChipAnimation = (
    fromRect: DOMRect,
    toRect: DOMRect,
    delay: number = 0,
    count: number = 1,
    preciseAlignEnd: boolean = false,
    preciseAlignStart: boolean = false,
    stageEl: HTMLElement | null,
    flyingChips: { value: FlyingChip[] },
    flyingChipIdRef: { current: number }
) => {
    if (!stageEl) return;
    // chips-layer 是在 ratio-box 内部，所以需要使用 ratio-box 的位置来计算
    const ratioBoxEl = stageEl.querySelector('.ratio-box') as HTMLElement | null;
    if (!ratioBoxEl) return;
    const ratioBoxRect = ratioBoxEl.getBoundingClientRect();
    // 计算相对于 ratio-box 的位置（chips-layer 是相对于 ratio-box 定位的）
    // getBoundingClientRect() 返回的是相对于视口的位置，所以直接相减即可得到相对位置
    const chipSize = 14; // 筹码图片大小
    const chipHalf = chipSize / 2;
    
    // 如果精确对齐，需要减去筹码大小的一半来让中心点对齐
    const startOffsetX = preciseAlignStart ? chipHalf : 0;
    const startOffsetY = preciseAlignStart ? chipHalf : 0;
    const endOffsetX = preciseAlignEnd ? chipHalf : 0;
    const endOffsetY = preciseAlignEnd ? chipHalf : 0;
    
    const baseStartX = fromRect.left + fromRect.width / 2 - ratioBoxRect.left - startOffsetX;
    const baseStartY = fromRect.top + fromRect.height / 2 - ratioBoxRect.top - startOffsetY;
    const baseEndX = toRect.left + toRect.width / 2 - ratioBoxRect.left - endOffsetX;
    const baseEndY = toRect.top + toRect.height / 2 - ratioBoxRect.top - endOffsetY;

    const duration = 700; // 与 CSS 动画时长保持一致
    const oneDelayStep = 40; // 同一批筹码内部的时间错位

    for (let i = 0; i < count; i++) {
        const id = flyingChipIdRef.current++;

        // 如果终点精确对齐，不添加随机偏移；否则添加轻微随机偏移
        const offset = preciseAlignEnd ? 0 : (Math.random() - 0.5) * 20; // -10 ~ 10
        const startX = baseStartX + offset;
        const endX = baseEndX + offset;

        const chip: FlyingChip = {
            id,
            style: {
                '--start-x': `${startX}px`,
                '--start-y': `${baseStartY}px`,
                '--end-x': `${endX}px`,
                '--end-y': `${baseEndY}px`,
                animationDelay: `${delay + i * oneDelayStep}ms`,
            },
        };

        flyingChips.value.push(chip);

        // 动画结束后移除
        setTimeout(() => {
            flyingChips.value = flyingChips.value.filter(it => it.id !== id);
        }, duration + delay + i * oneDelayStep + 80);
    }
};

/**
 * 结算时：输家筹码先飞向庄家，再从庄家飞向赢家；平局时筹码飞向庄家再飞回
 * @param data 结算数据数组
 * @param getPlayerComponentById 获取玩家组件实例的函数
 * @param dealerWagerRef 庄家筹码组件引用
 * @param stageEl 舞台元素引用
 * @param flyingChips 飞筹码数组引用
 * @param flyingChipIdRef 飞筹码ID计数器引用对象
 */
export const playChipsSettlementAnimation = (
    data: any[] = [],
    getPlayerComponentById: (id: number | string) => any | null,
    dealerWagerRef: any,
    stageEl: HTMLElement | null,
    flyingChips: { value: FlyingChip[] },
    flyingChipIdRef: { current: number }
) => {
    if (!import.meta.client) return;

    const dealerEl = (dealerWagerRef as any)?.$el as HTMLElement | undefined;
    if (!dealerEl) return;
    // 获取庄家筹码图片的位置（.coin-box 内的第一个 img 标签）
    const dealerCoinBox = dealerEl.querySelector('.coin-box') as HTMLElement | null;
    const dealerChipImg = dealerCoinBox?.querySelector('img') as HTMLElement | null;
    const dealerRect = dealerChipImg?.getBoundingClientRect() || dealerCoinBox?.getBoundingClientRect() || dealerEl.getBoundingClientRect();

    // winners: diff > 0, losers: diff < 0, draws: diff === 0
    const winners = data
        .filter(it => it.player?.id && Number(it.player.diff) > 0)
        .map(it => it.player);
    const losers = data
        .filter(it => it.player?.id && Number(it.player.diff) < 0)
        .map(it => it.player);
    const draws = data
        .filter(it => it.player?.id && Number(it.player.diff) === 0)
        .map(it => it.player);

    // 如果没有赢家、输家和平局玩家，直接返回
    if (!winners.length && !losers.length && !draws.length) return;

    const chipDuration = 700;  // 对应 CSS 中 chip-fly 动画时长（ms）
    const chipsPerPlayer = 3;  // 每个玩家飞向庄家的筹码数量
    const playerInterval = 80;  // 每个玩家的筹码飞出间隔

    // 阶段一：输家和平局玩家 -> 庄家（终点精确对齐，完全重合）
    const phase1Players = [...losers, ...draws];
    phase1Players.forEach((player, idx) => {
        const comp = getPlayerComponentById(player.id);
        const rect = comp?.getWagerRect?.();
        if (!rect) return;
        createChipAnimation(rect, dealerRect, idx * playerInterval, chipsPerPlayer, true, false, stageEl, flyingChips, flyingChipIdRef); // 终点精确对齐，起点不精确对齐
    });
    playMusic('to_dealer');

    // 阶段二必须在 settled 事件后 800ms 准时开始（即 gameOver 后 800ms）
    // 动画函数在 gameOver 后 400ms 调用，所以阶段二固定使用 400ms 延迟
    // 这样无论阶段一是否完成，阶段二都会在 settled 事件后 800ms 准时开始
    const targetPhase2StartTime = 400; // 从动画函数调用开始计算，固定 400ms 后 = gameOver 后 800ms
    const baseDelay = targetPhase2StartTime; // 固定使用 400ms，确保与全局推送同步

    // 阶段二：庄家 -> 赢家（起点精确对齐，终点飞到玩家头像，有随机偏移）
    const winnerInterval = 80;
    if (winners.length > 0) {
        winners.forEach((player, idx) => {
            const comp = getPlayerComponentById(player.id);
            // 获取玩家头像位置，而不是筹码位置
            const rect = comp?.getAvatarRect?.();
            if (!rect) return;
            createChipAnimation(dealerRect, rect, baseDelay + idx * winnerInterval, chipsPerPlayer, false, true, stageEl, flyingChips, flyingChipIdRef); // 终点不精确对齐，起点精确对齐
        });
    }

    // 阶段二（平局）：庄家 -> 平局玩家（起点精确对齐，终点飞回玩家筹码位置，有随机偏移）
    if (draws.length > 0) {
        const drawInterval = 80;
        const pauseBetweenPhases = 100; // 赢家和平局玩家之间的短暂停顿
        const drawBaseDelay = winners.length > 0 
            ? baseDelay + winners.length * winnerInterval + chipDuration + pauseBetweenPhases
            : baseDelay;
        draws.forEach((player, idx) => {
            const comp = getPlayerComponentById(player.id);
            const rect = comp?.getWagerRect?.();
            if (!rect) return;
            createChipAnimation(dealerRect, rect, drawBaseDelay + idx * drawInterval, chipsPerPlayer, false, true, stageEl, flyingChips, flyingChipIdRef); // 终点不精确对齐，起点精确对齐
        });
    }
    playMusic('to_winner');
};

/**
 * 执行牌的亮牌高亮动画效果
 * @param borderElement 边框元素引用（HTMLElement）
 */
export const playHighlightEffect = async (borderElement: HTMLElement | null | undefined) => {
    if (!borderElement) return;

    const gsap = await loadGSAP();
    
    // 先停止可能的旧动画
    gsap.killTweensOf(borderElement);

    // 先显示边框
    gsap.set(borderElement, { opacity: 1 });

    // --- 呼吸动画 ---
    const tl = gsap.timeline();
    
    tl.to(borderElement, {
        duration: 0.3,
        ease: "power1.inOut",
    })
    // --- 停留 X 秒 ---
    tl.to({}, { duration: 0.3 });
     // --- 自动复原 ---
    tl.to(borderElement, {
        opacity: 0,
        duration: 0.3,
        ease: "power2.out"
    });
};

